import {
  currentOffer as currentOfferApi,
  getDocument as getDocumentApi,
  publishAdminDocument as publishAdminDocumentApi,
  publishAdminOffer as publishAdminOfferApi,
} from '@api/generated/sdk.gen';
import { getConfig } from '@shared/lib/config';
import { isAxiosError } from 'axios';

/**
 * Слой доступа к документам платформы: публичная оферта и справочные
 * документы (FAQ поддержки и будущие правила/политики).
 *
 * Бэк хранит оба вида в одном механизме версий: загрузка PDF создаёт новую
 * редакцию и деактивирует прежнюю, публичные эндпоинты всегда отдают
 * актуальную. У оферты свои эндпоинты (`/api/offers/**`) — акцепты пользователей;
 * справочные документы ходят по общему пути `/api/documents/{kind}`. Этот слой
 * прячет различие за единым интерфейсом «текущая редакция + публикация».
 */

/** Тип документа. `offer` — особый (свои эндпоинты); остальные — справочные (`/api/documents/{kind}`). */
export type DocumentKind = 'offer' | 'support-faq';

/** Метаданные редакции документа в едином виде для карточки. */
export type DocumentMeta = {
  version?: number;
  sha256?: string;
  publishedAt?: string;
};

function isNotFound(err: unknown): boolean {
  return isAxiosError(err) && err.response?.status === 404;
}

/**
 * Загружает метаданные актуальной редакции документа.
 * Публичный эндпоинт, токен не нужен.
 *
 * @returns `null`, если документ ещё не опубликован (404)
 */
export async function fetchCurrent(kind: DocumentKind): Promise<DocumentMeta | null> {
  try {
    if (kind === 'offer') {
      const res = await currentOfferApi();
      const d = res.data;
      return { version: d?.version, sha256: d?.sha256, publishedAt: d?.publishedAt };
    }
    const res = await getDocumentApi({ path: { kind } });
    const d = res.data;
    return { version: d?.version, sha256: d?.sha256, publishedAt: d?.updatedAt };
  } catch (err) {
    if (isNotFound(err)) return null;
    throw err;
  }
}

/**
 * Публикует новую редакцию документа из PDF-файла (multipart `file`).
 * Требует право `settings.write`.
 *
 * @returns метаданные опубликованной редакции
 */
export async function publish(kind: DocumentKind, file: File): Promise<DocumentMeta> {
  if (kind === 'offer') {
    const res = await publishAdminOfferApi({ body: { file } });
    const d = res.data;
    return { version: d?.version, sha256: d?.sha256, publishedAt: d?.publishedAt };
  }
  const res = await publishAdminDocumentApi({ path: { kind }, body: { file } });
  const d = res.data;
  return { version: d?.version, sha256: d?.sha256, publishedAt: d?.updatedAt };
}

/** Публичный URL PDF актуальной редакции — для «Открыть PDF» в новой вкладке. */
export function publicFileUrl(kind: DocumentKind): string {
  const base = getConfig().apiBaseUrl.replace(/\/$/, '');
  return kind === 'offer' ? `${base}/api/offers/current/file` : `${base}/api/documents/${kind}/file`;
}
